import { getDateFormat, getFullName } from '../../../../utils/common-methods';
import FIELD_TYPE from '../../../../constants/fieldType';
import { extractPostingSublocationBuckets } from './sublocationBuckets';
import { resolveProductType } from './postingMetadata';
import { resolveX3ProductName, toTrimmedString } from './shared';

const NOT_AVAILABLE = FIELD_TYPE.NOT_APPLICABLE;

const formatPostedWeek = (startDate, endDate) => {
  if (!startDate || !endDate) return NOT_AVAILABLE;
  const start = getDateFormat(startDate);
  const end = getDateFormat(endDate);
  return start && end ? `${start} - ${end}` : NOT_AVAILABLE;
};

const formatPostingDateValue = (value) => (value ? getDateFormat(value) || NOT_AVAILABLE : NOT_AVAILABLE);

export const transformPostingData = (posting, usersMap = {}, productCategoriesMap = {}) => {
  if (!posting || typeof posting !== 'object') {
    return {
      id: null,
      postingId: NOT_AVAILABLE,
      postedWeek: NOT_AVAILABLE,
      postingDate: NOT_AVAILABLE,
      productType: NOT_AVAILABLE,
      sublocations: [],
      createdBy: NOT_AVAILABLE,
      postedOn: NOT_AVAILABLE,
      originalData: posting ?? null,
    };
  }

  const x3ProductName = toTrimmedString(posting?.x3ProductName || posting?.x3_product_name).toUpperCase();
  const productCategory =
    productCategoriesMap?.[String(posting?.productId)] ||
    (x3ProductName ? productCategoriesMap?.[`x3:${x3ProductName}`] : null);
  const productCategoryName = resolveProductType(posting) || productCategory?.x3_productname || productCategory?.name;
  const createdById = posting?.createdBy ?? posting?.approvedBy ?? posting?.created_by ?? posting?.approved_by ?? null;
  const createdByUserFromApi = posting?.createdByUser;
  const createdByUser = createdByUserFromApi || (createdById ? usersMap[String(createdById)] : null);
  const createdByFullName = createdByUser
    ? toTrimmedString(getFullName(toTrimmedString(createdByUser.firstName), toTrimmedString(createdByUser.lastName)))
    : '';
  const fallbackCreatedByName = toTrimmedString(posting?.createdByName) || toTrimmedString(posting?.postedByName);
  const createdByIdentifier =
    createdById !== null && createdById !== undefined && createdById !== '' ? String(createdById) : NOT_AVAILABLE;
  const createdByName =
    (createdByFullName && createdByFullName !== NOT_AVAILABLE ? createdByFullName : '') ||
    fallbackCreatedByName ||
    createdByIdentifier;

  const postedOnDate = posting.updatedAt || posting.createdAt;
  const { selectedSublocations, unselectedSublocations } = extractPostingSublocationBuckets(posting);
  const sublocationNames = selectedSublocations
    .map((sublocation) => toTrimmedString(sublocation?.name))
    .filter(Boolean);

  return {
    id: posting.id,
    postingId: posting.postingId || NOT_AVAILABLE,
    postedWeek: formatPostedWeek(posting.startDate, posting.endDate),
    postingDate: formatPostingDateValue(posting.postingDate),
    productType: productCategoryName || NOT_AVAILABLE,
    x3ProductName: x3ProductName || resolveX3ProductName(productCategory) || '',
    sublocations: sublocationNames,
    selectedSublocations,
    unselectedSublocations,
    createdBy: createdByName,
    postedOn: getDateFormat(postedOnDate) || NOT_AVAILABLE,
    originalData: posting,
  };
};

export const buildPostingCacheKey = (posting = {}) => {
  const {
    id = '',
    postingId = '',
    productId = '',
    x3ProductName = '',
    createdBy = '',
    approvedBy = '',
    startDate = '',
    endDate = '',
    createdAt = '',
    updatedAt = '',
  } = posting || {};
  return [
    id,
    postingId,
    productId,
    x3ProductName,
    createdBy || approvedBy,
    startDate,
    endDate,
    updatedAt || createdAt,
  ].join('|');
};
